import {
  SEOHead,
  TopNavbar,
  CardSlider,
  EventGrid,
  Footer,
} from "@components/organisms";
import { Text, CategoryButton } from "@components/atoms";
import { ErrorMessage, LoadingPage } from "@components/molecules";
import { useEvents, useEventFilters } from "@modules/events/hooks";
import type { Event as AppEvent } from "@/types";

/**
 * Home / landing page.
 * Full event discovery page with navbar, category filters,
 * featured slider, event grid, and footer.
 */
export function HomePage() {
  const { data: events, isLoading, error, refetch } = useEvents();
  const {
    selectedCategories,
    setSelectedCategories,
    dateRange,
    setDateRange,
    priceRange,
    setPriceRange,
    filteredEvents,
    hasActiveFilters,
    clearAll,
  } = useEventFilters();

  const categories = [
    {
      icon: "/svg/sports_soccer.svg",
      label: "Deportes",
      value: "deportes" as const,
    },
    { icon: "/svg/nightlife.svg", label: "Fiestas", value: "fiestas" as const },
    {
      icon: "/svg/piano.svg",
      label: "Conciertos",
      value: "conciertos" as const,
    },
    {
      icon: "/svg/theater_comedy.svg",
      label: "Stand-up",
      value: "stand-up" as const,
    },
    {
      icon: "/svg/otros.svg",
      label: "Otros",
      value: "otros" as const,
    },
  ];

  const handleCategoryClick = (
    value: "deportes" | "fiestas" | "conciertos" | "stand-up" | "otros",
  ) => {
    if (selectedCategories.includes(value)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== value));
    } else {
      setSelectedCategories([...selectedCategories, value]);
    }
  };

  const toLocalDate = (iso?: string | null) => {
    if (!iso) return null;
    const [y = 0, m = 0, d = 0] = iso.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const today = new Date();
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const isUpcoming = (event: AppEvent) => {
    const start = toLocalDate(event.dateevent);
    const end = toLocalDate(event.dateend ?? null) ?? start;

    // Sin fecha → mostrar siempre (abonos, eventos continuos, etc.)
    if (!start && !end) return true;

    return !end || end >= todayLocal;
  };

  const upcomingEvents = (events ?? []).filter(isUpcoming);

  return (
    <>
      <SEOHead
        title="Entradas para eventos en Ecuador"
        description="TicketIn es la plataforma líder de venta de entradas en Ecuador. Compra boletos para conciertos, deportes, teatro y más de forma segura y rápida."
        canonical="https://ticketin.ec/"
      />

      <div className="flex min-h-screen flex-col bg-gradient-to-b from-neutral-60 to-primary-110">
        {/* Top Navbar */}
        <TopNavbar events={upcomingEvents ?? []} />

        {/* Loading state */}
        {isLoading && <LoadingPage withGradient />}

        {/* Error state */}
        {error && (
          <div className="flex flex-1 items-center justify-center px-8">
            <ErrorMessage error={error} onRetry={refetch} />
          </div>
        )}

        {upcomingEvents && (
          <>
            {/* Category buttons */}
            <div className="flex justify-center gap-6 sm:gap-10 md:gap-[63px] py-8 px-4 flex-wrap">
              {categories.map((cat) => (
                <CategoryButton
                  key={cat.label}
                  iconSrc={cat.icon}
                  label={cat.label}
                  iconSize={55}
                  hasActiveSelection={selectedCategories.length > 0}
                  isSelected={selectedCategories.includes(cat.value)}
                  onClick={() => handleCategoryClick(cat.value)}
                />
              ))}
            </div>

            {/* Card slider - hidden when filters are active */}
            {!hasActiveFilters && (
              <>
                <div className="px-8 pb-4">
                  <Text variant="h2" as="h2" className="text-white text-center">
                    Próximos eventos
                  </Text>
                </div>
                <CardSlider events={upcomingEvents} />
              </>
            )}

            {/* Event grid with filter controls and filtered events */}
            <EventGrid
              events={filteredEvents(upcomingEvents)}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              priceRange={priceRange}
              onPriceRangeChange={setPriceRange}
              selectedCategories={selectedCategories}
              onCategoriesChange={setSelectedCategories}
              onClearAll={clearAll}
            />
          </>
        )}

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}
